import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRIORITIES } from "@/lib/meta";
import RejectLeadDialog from "@/components/RejectLeadDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowUpRight, XCircle, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [{ key: "new", label: "New" }, { key: "follow_up", label: "Follow Up" }, { key: "duplicate", label: "Duplicate" }];

export default function LeadBulkBar({ selection, count, onClear, onDone }) {
  const { user } = useAuth();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const run = async (action, extra = {}) => {
    try {
      const { data } = await api.post("/leads/bulk", { action, ...selection, ...extra });
      const parts = [`${data.affected} lead(s) updated`];
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      if (data.errors?.length) parts.push(`${data.errors.length} failed`);
      toast.success(parts.join(" · "));
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const CONFIRMS = {
    promote: { title: `Promote ${count} lead(s)?`, desc: "Each lead becomes its own new Property + Deal in the New stage. Rejected, duplicate and already-promoted leads are skipped.", btn: "Promote all" },
    delete: { title: `Delete ${count} lead(s)?`, desc: "This permanently removes the leads. This can't be undone.", btn: "Delete permanently" },
  };

  return (
    <div className="flex items-center gap-2 flex-wrap bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-lg px-3 py-2 mb-3" data-testid="bulk-bar">
      <span className="text-sm font-semibold text-gray-900" data-testid="bulk-count">{count} selected</span>
      <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 mr-2" data-testid="bulk-clear"><X className="h-3.5 w-3.5" /> Clear</button>
      <Button size="sm" variant="outline" onClick={() => setConfirm("promote")} data-testid="bulk-promote"><ArrowUpRight className="h-3.5 w-3.5" /> Promote</Button>
      <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setRejectOpen(true)} data-testid="bulk-reject"><XCircle className="h-3.5 w-3.5" /> Reject</Button>
      <Select value="" onValueChange={(v) => run("set_status", { value: v })}>
        <SelectTrigger className="h-8 w-36 bg-white" data-testid="bulk-status"><SelectValue placeholder="Set status" /></SelectTrigger>
        <SelectContent className="bg-white">{STATUS_OPTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value="" onValueChange={(v) => run("set_priority", { value: v })}>
        <SelectTrigger className="h-8 w-36 bg-white" data-testid="bulk-priority"><SelectValue placeholder="Set priority" /></SelectTrigger>
        <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
      </Select>
      {user?.role === "admin" && (
        <Button size="sm" variant="outline" className="text-rose-600 ml-auto" onClick={() => setConfirm("delete")} data-testid="bulk-delete"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
      )}

      <RejectLeadDialog open={rejectOpen} onOpenChange={setRejectOpen} count={count}
        onConfirm={async (code, note) => { await run("reject", { code, note }); setRejectOpen(false); toast.info("Rejected leads moved to Properties › Rejected Archive"); }} />
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="bg-white" data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{CONFIRMS[confirm]?.title}</AlertDialogTitle>
            <AlertDialogDescription>{CONFIRMS[confirm]?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className={confirm === "delete" ? "bg-rose-600 hover:bg-rose-700" : ""} onClick={() => { const a = confirm; setConfirm(null); run(a); }} data-testid="bulk-confirm-ok">{CONFIRMS[confirm]?.btn}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
